import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { AutocompleteField } from '~/components/AutocompleteField/AutocompleteField.tsx';
import { tasksPageText } from '../constants.ts';
import { tasksPageStyles } from '../styles.ts';
import { buildEmailOptions, normalizeEmail } from './utils.ts';

interface ITaskAssigneeModalProps {
  currentEmail?: string | null;
  errorText?: string | null;
  isLoading?: boolean;
  isOpen: boolean;
  isSaving?: boolean;
  supervisorEmails?: string[];
  onClose: () => void;
  onSubmit: (email: string | null) => void;
}

const TaskAssigneeModal: React.FC<ITaskAssigneeModalProps> = ({
  currentEmail,
  errorText,
  isLoading = false,
  isOpen,
  isSaving = false,
  supervisorEmails = [],
  onClose,
  onSubmit,
}) => {
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const initialEmail = normalizeEmail(currentEmail);
  const normalizedSelectedEmail = normalizeEmail(selectedEmail);
  const isBusy = isLoading || isSaving;
  const hasChanged = normalizedSelectedEmail !== initialEmail;
  const options = useMemo(
    () => buildEmailOptions(supervisorEmails, currentEmail),
    [currentEmail, supervisorEmails]
  );
  const selectedOption = options.find((option) => option.value === normalizedSelectedEmail) ?? null;

  useEffect(() => {
    if (isOpen) {
      setSelectedEmail(initialEmail || null);
    }
  }, [initialEmail, isOpen]);

  const handleSubmit = () => {
    if (isBusy || !hasChanged) {
      return;
    }

    onSubmit(normalizedSelectedEmail || null);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isBusy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={tasksPageStyles.assigneeDialog}
    >
      <DialogTitle sx={tasksPageStyles.assigneeDialogTitle}>
        {tasksPageText.assignExecutorDialogTitle}
      </DialogTitle>
      <DialogContent sx={tasksPageStyles.assigneeDialogContent}>
        {isLoading ? (
          <Box sx={tasksPageStyles.assigneeDialogLoading}>
            <CircularProgress size={24} />
            <Typography sx={tasksPageStyles.assigneeDialogText}>
              {tasksPageText.assignExecutorLoadingText}
            </Typography>
          </Box>
        ) : (
          <>
            <AutocompleteField
              options={options}
              value={selectedOption}
              onChange={(option) => setSelectedEmail(option?.value ?? null)}
              label={tasksPageText.assignExecutorFieldLabel}
              noOptionsText={tasksPageText.assignExecutorEmptyText}
              disabled={isSaving}
            />
            {errorText && (
              <Typography sx={tasksPageStyles.assigneeDialogError}>{errorText}</Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={tasksPageStyles.assigneeDialogActions}>
        <Button onClick={onClose} disabled={isBusy}>
          {tasksPageText.assignExecutorCancelButton}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isBusy || !hasChanged}>
          {isSaving ? tasksPageText.actionInProgressLabel : tasksPageText.assignExecutorSaveButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskAssigneeModal;
